import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

import { useSelector } from 'react-redux';
import { selectCommandByNodeId, State } from '@store/index';
import { LeftNavbar, TopNavbar } from '../../components/Navbars';
import { useParams } from 'react-router-dom';
import { CompletionSidebar } from '@components/Flow/Nodes/Standard/Completion/Sidebar';
import { ToolSidebar } from '@components/Flow/Nodes/Standard/Tool/Sidebar';
import { RequestSidebar } from '@components/Flow/Nodes/Trigger/Request/Sidebar';
import { ResponseSidebar } from '@components/Flow/Nodes/Standard/Response/Sidebar';
import { SwitchSidebar } from '@components/Flow/Nodes/Logic/Switch/Sidebar';
import { BranchSidebar } from '@components/Flow/Nodes/Logic/Branch/Sidebar';
import { HTTPSidebar } from '@components/Flow/Nodes/Standard/HTTP/Sidebar';
import { ModuleSidebar } from '@components/Flow/Nodes/Trigger/Module/Sidebar';
import { ModNodeSidebar } from '@components/Flow/Nodes/Standard/ModNode/Sidebar';
import { InboundCallSidebar } from '@components/Flow/Nodes/Trigger/InboundCall/Sidebar';
import { HangupCallSidebar } from '@components/Flow/Nodes/CallControl/HangupCall/Sidebar';
import { AnswerCallSidebar } from '@components/Flow/Nodes/CallControl/AnswerCall/Sidebar';
import { SpeakTextSidebar } from '@components/Flow/Nodes/CallControl/SpeakText/Sidebar';
import { TranscriptionStartSidebar } from '@components/Flow/Nodes/CallControl/TranscriptionStart/Sidebar';
import { GatherUsingAISidebar } from '@components/Flow/Nodes/CallControl/GatherUsingAI/Sidebar';
import { TranscriptionStopSidebar } from '@components/Flow/Nodes/CallControl/TranscriptionStop/Sidebar';
import { PlaybackStartSidebar } from '@components/Flow/Nodes/CallControl/PlaybackStart/Sidebar';
import { PlaybackStopSidebar } from '@components/Flow/Nodes/CallControl/PlaybackStop/Sidebar';
import { NoiseSuppressionSidebar } from '@components/Flow/Nodes/CallControl/NoiseSuppression/Sidebar';
import { StoreSidebar } from '@components/Flow/Nodes/StoreMessage/Sidebar';
import { NoiseSuppressionStopSidebar } from '@components/Flow/Nodes/CallControl/NoiseSuppressionStop/Sidebar';
import { DialSidebar } from '@components/Flow/Nodes/CallControl/Dial/Sidebar';
import { RejectCallSidebar } from '@components/Flow/Nodes/CallControl/RejectCall/Sidebar';
import { TransferCallSidebar } from '@components/Flow/Nodes/CallControl/TransferCall/Sidebar';
import { JumpToSidebar } from '@components/Flow/Nodes/Logic/JumpTo/Sidebar';
import { DelaySidebar } from '@components/Flow/Nodes/Utilities/Delay/Sidebar';
import { Base64TransformerSidebar } from '@components/Flow/Nodes/Utilities/Base64Transformer/Sidebar';
import { DateTimeSidebar } from '@components/Flow/Nodes/Utilities/Datetime/Sidebar';
import { StreamingStartSidebar } from '@components/Flow/Nodes/CallControl/StreamingStart/Sidebar';
import { StreamingStopSidebar } from '@components/Flow/Nodes/CallControl/StreamingStop/Sidebar';
import { InboundMessageSidebar } from '@components/Flow/Nodes/Trigger/InboundMessage/Sidebar';
import { SendMessageSidebar } from '@components/Flow/Nodes/Messaging/SendMessage/Sidebar';
import { BridgeSidebar } from '@components/Flow/Nodes/CallControl/Bridge/Sidebar';
import { RecordingStartSidebar } from '@components/Flow/Nodes/CallControl/RecordingStart/Sidebar';
import { RecordingStopSidebar } from '@components/Flow/Nodes/CallControl/RecordingStop/Sidebar';
import { RecordingResumeSidebar } from '@components/Flow/Nodes/CallControl/RecordingResume/Sidebar';
import { RecordingPauseSidebar } from '@components/Flow/Nodes/CallControl/RecordingPause/Sidebar';
import { AssistantRouterSidebar } from '@components/Flow/Nodes/Standard/AssistantRouter/Sidebar';
import { AssistantSidebar } from '@components/Flow/Nodes/Standard/Assistant/Sidebar';

const Container = styled.div`
  min-height: 100vh;
  max-height: 100vh;

  align-items: stretch;
  display: flex;
  flex-direction: row;
  height: 100%;
  padding: 0;
`;

const Wrapper = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;

  flex: 1 1 auto;
  min-width: 0;
  padding-left: 60px;
`;

const Body = styled.div<{ showSidebar?: boolean }>`
  display: ${({ showSidebar }) => (showSidebar ? 'grid' : 'flex')};
  grid-template-columns: 7fr 2.5fr;
  height: calc(100vh - 61px);
`;

interface Props {
  body: React.ReactNode;
  abortControllerRef: React.MutableRefObject<AbortController | null>;
}

export const LayoutFlow: React.FC<Props> = ({ body, abortControllerRef }) => {
  const { service_id } = useParams<{ service_id: string }>();

  const editingName = useSelector((state: State) => state.flow.editingName);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const command = useSelector((state: State) => selectCommandByNodeId(state, state.tabs.active.selectedNode));
  const services = useSelector((state: State) => state.flow.services);
  const service = services.find((service) => service.service_id === service_id);

  useEffect(() => {
    if (editingName) inputRef.current?.focus();
  }, [editingName]);

  return (
    <Container>
      <LeftNavbar />
      <Wrapper>
        <TopNavbar header={service?.service_name || 'Workspace'} />
        <Body showSidebar={Boolean(command)}>
          {body}
          {command?.config.command_id === 'call.initiated' && (
            <InboundCallSidebar abortControllerRef={abortControllerRef} />
          )}
          {command?.config.command_id === 'message.received' && (
            <InboundMessageSidebar abortControllerRef={abortControllerRef} />
          )}
          {command?.config.command_id === 'call.bridge' && <BridgeSidebar />}
          {command?.config.command_id === 'call.recording.stop' && <RecordingStopSidebar />}
          {command?.config.command_id === 'call.recording.start' && <RecordingStartSidebar />}
          {command?.config.command_id === 'call.recording.resume' && <RecordingResumeSidebar />}
          {command?.config.command_id === 'call.recording.pause' && <RecordingPauseSidebar />}
          {command?.config.command_id === 'message.initiated' && <SendMessageSidebar />}
          {command?.config.command_id === 'streaming.start' && <StreamingStartSidebar />}
          {command?.config.command_id === 'streaming.stop' && <StreamingStopSidebar />}
          {command?.config.command_id === 'call.reject' && <RejectCallSidebar />}
          {command?.config.command_id === 'call.playback.started' && <PlaybackStartSidebar />}
          {command?.config.command_id === 'call.dial' && <DialSidebar />}
          {command?.config.command_id === 'call.transfer' && <TransferCallSidebar />}
          {command?.config.command_id === 'call.playback.ended' && <PlaybackStopSidebar />}
          {command?.config.command_id === 'call.answered' && <AnswerCallSidebar />}
          {command?.config.command_id === 'call.noise_suppression' && <NoiseSuppressionSidebar />}
          {command?.config.command_id === 'call.noise_suppression_stop' && <NoiseSuppressionStopSidebar />}
          {command?.config.command_id === 'call.speak' && <SpeakTextSidebar />}
          {command?.config.command_id === 'call.transcription' && <TranscriptionStartSidebar />}
          {command?.config.command_id === 'call.transcription.stop' && <TranscriptionStopSidebar />}
          {command?.config.command_id === 'call.ai_gather' && <GatherUsingAISidebar />}
          {command?.config.command_id === 'call.hangup' && <HangupCallSidebar />}
          {command?.config.command_id === 'assistant.single' && <AssistantSidebar />}
          {command?.config.command_id === 'assistant.router' && <AssistantRouterSidebar />}
          {command?.config.command_id === 'llm.completion' && <CompletionSidebar />}
          {command?.config.command_id === 'llm.tool' && <ToolSidebar />}
          {command?.config.command_id === 'http.request' && <RequestSidebar abortControllerRef={abortControllerRef} />}
          {command?.config.command_id === 'workflow.module' && (
            <ModuleSidebar abortControllerRef={abortControllerRef} />
          )}
          {command?.config.command_id === 'node.module' && <ModNodeSidebar />}
          {command?.config.command_id === 'http.client' && <HTTPSidebar />}
          {command?.config.command_id === 'http.response' && <ResponseSidebar />}
          {command?.config.command_id === 'logic.jump' && <JumpToSidebar />}
          {command?.config.command_id === 'logic.switch' && <SwitchSidebar />}
          {command?.config.command_id === 'logic.branch' && <BranchSidebar />}
          {command?.config.command_id === 'database.store_message' && <StoreSidebar />}
          {command?.config.command_id === 'utility.delay' && <DelaySidebar />}
          {command?.config.command_id === 'utility.base64' && <Base64TransformerSidebar />}
          {command?.config.command_id === 'utility.datetime' && <DateTimeSidebar />}
        </Body>
      </Wrapper>
    </Container>
  );
};
